import React, { useEffect, useRef } from 'react';

/**
 * Lightweight WYSIWYG editor backed by contenteditable + execCommand.
 *
 * Why not pull in TipTap / Quill / Slate?
 *   • Each adds 100–300 KB to the bundle.
 *   • We only need the basics for an email body: B / I / U, lists, link,
 *     clear-formatting. execCommand is deprecated on paper but still
 *     supported across every browser we ship to and likely will be for
 *     years — Mozilla's "deprecation" note is about formal removal which
 *     hasn't been scheduled.
 *
 * Controlled-mode behaviour:
 *   The component is *not* a fully controlled input. We push `value` into
 *   the contenteditable DOM only when it changes from the outside (e.g.
 *   when the user clicks "Reset to default template"). Re-syncing on every
 *   keystroke would reset the caret on every character typed.
 */

export interface RichTextEditorProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  minHeight?: number;        // px
  className?: string;
}

const ToolbarButton: React.FC<{
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, onClick, children }) => (
  <button
    type="button"
    title={title}
    // Prevent the contenteditable area from losing focus when the user
    // clicks a toolbar button — execCommand only works on the focused element.
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="px-2 py-1 text-sm text-gray-700 hover:bg-gray-200 rounded transition-colors"
  >
    {children}
  </button>
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value, onChange, placeholder, minHeight = 200, className,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastEmittedRef = useRef<string>('');

  // Push external value into the DOM only when it actually diverges, so
  // typing doesn't trigger an outer-state-driven re-render that resets the
  // caret position on every keystroke.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastEmittedRef.current && value !== el.innerHTML) {
      el.innerHTML = value || '';
      lastEmittedRef.current = value;
    }
  }, [value]);

  const exec = (cmd: string, arg?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    handleInput();
  };

  const handleInput = () => {
    const html = editorRef.current?.innerHTML ?? '';
    lastEmittedRef.current = html;
    onChange(html);
  };

  const insertLink = () => {
    const url = window.prompt('Link URL (include https://)', 'https://');
    if (!url || url === 'https://') return;
    exec('createLink', url);
  };

  const clearFormatting = () => {
    exec('removeFormat');
    exec('unlink');
  };

  return (
    <div className={`border border-gray-300 rounded-md focus-within:ring-1 focus-within:ring-blue-500 bg-white ${className ?? ''}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-gray-200 bg-gray-50 rounded-t-md">
        <ToolbarButton title="Bold (Ctrl+B)" onClick={() => exec('bold')}>
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton title="Italic (Ctrl+I)" onClick={() => exec('italic')}>
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton title="Underline (Ctrl+U)" onClick={() => exec('underline')}>
          <span style={{ textDecoration: 'underline' }}>U</span>
        </ToolbarButton>
        <span className="mx-1 w-px h-4 bg-gray-300" />
        <ToolbarButton title="Bulleted list" onClick={() => exec('insertUnorderedList')}>
          • List
        </ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => exec('insertOrderedList')}>
          1. List
        </ToolbarButton>
        <span className="mx-1 w-px h-4 bg-gray-300" />
        <ToolbarButton title="Insert link" onClick={insertLink}>
          🔗 Link
        </ToolbarButton>
        <ToolbarButton title="Clear formatting" onClick={clearFormatting}>
          ✕ Format
        </ToolbarButton>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        // Most pastes from Word / Outlook bring 5 KB of garbage CSS along.
        // Strip back to plain text so the user can apply our own formatting.
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        data-placeholder={placeholder}
        className="px-3 py-2 text-sm outline-none overflow-y-auto rich-text-editor-area"
        style={{ minHeight }}
      />
      <style>{`
        .rich-text-editor-area:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-text-editor-area p { margin: 0 0 8px 0; }
        .rich-text-editor-area ul, .rich-text-editor-area ol { margin: 8px 0 8px 24px; }
        .rich-text-editor-area a { color: #2563eb; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
